import type { IPLWidgetControllerCards } from "@swiggy-private/dls-react-mxn/dist/WidgetController/PLWidgetController/type";
import { VegClassifier } from "@swiggy-private/dls-web-recipe-common-web/dist/components/FoodItem/types";
import { VegType } from "@swiggy-private/dls-web-recipe-common-web/dist/components/ItemListWidget/types";
import type { AppliedFilters, CrossSellConfig } from "@swiggy-private/portal-dineout-context";
import {
    CROSS_SELL_CONSTRAINT_DEFAULTS,
    NONVEG_FACET_QUERY_ID,
    NONVEG_PREFERENCE,
    VEG_FACET_QUERY_ID,
    VEG_PREFERENCE,
} from "./constants";
import { getAllAppliedFilterValues } from "./crossSellAnalyticsHelpers";
import type { CrossSellNormalItem } from "./types";
import { ItemCard, WidgetTypes } from "../../types";

interface InnerCard {
    "@type"?: string;
    itemCards?: ItemCard[];
    categories?: Array<{ itemCards?: ItemCard[] }>;
}

const processItemCards = (itemCards: ItemCard[], map: Record<string, CrossSellNormalItem>) => {
    for (const item of itemCards) {
        if (item?.card?.["@type"] === WidgetTypes.DISH_WIDGET) {
            const info = item.card.info;
            if (info?.id === undefined) continue;
            const id = String(info.id);
            const analytics = item.card.analytics ?? info.analytics;
            map[id] = (analytics ? { ...info, analytics } : { ...info }) as CrossSellNormalItem;
        }
    }
};

export const buildDishMap = (
    cards: IPLWidgetControllerCards[]
): Record<string, CrossSellNormalItem> => {
    const map: Record<string, CrossSellNormalItem> = {};
    for (const card of cards) {
        const inner = card?.card?.card as InnerCard | undefined;
        if (!inner) continue;
        if (inner["@type"] === WidgetTypes.ITEM_CATEGORY_TYPE && Array.isArray(inner.itemCards)) {
            processItemCards(inner.itemCards, map);
        } else if (
            inner["@type"] === WidgetTypes.NESTED_ITEM_CATEGORY_TYPE &&
            Array.isArray(inner.categories)
        ) {
            for (const cat of inner.categories) {
                if (Array.isArray(cat.itemCards)) {
                    processItemCards(cat.itemCards, map);
                }
            }
        }
    }
    return map;
};

const matchesPreference = (item: CrossSellNormalItem, preference: string): boolean => {
    if (!preference) return true;
    const vegClassifier = item.itemAttribute?.vegClassifier;
    const isVeg = item.isVeg ?? item.vegType;

    if (item.itemAttribute?.vegClassifier === VegClassifier.NOT_APPLICABLE) {
        return false;
    }

    if (preference === VEG_PREFERENCE) {
        return vegClassifier === VegClassifier.VEG || isVeg === VegType.VEG;
    }
    if (preference === NONVEG_PREFERENCE) {
        return (
            vegClassifier === VegClassifier.NONVEG ||
            vegClassifier === VegClassifier.EGG ||
            isVeg === VegType.NON_VEG ||
            isVeg === VegType.EGG
        );
    }
    return true;
};

const derivePreferenceFromFilters = (appliedFilters?: AppliedFilters): string => {
    const allValues = getAllAppliedFilterValues(appliedFilters);
    if (allValues.includes(VEG_FACET_QUERY_ID)) return VEG_PREFERENCE;
    if (allValues.includes(NONVEG_FACET_QUERY_ID)) return NONVEG_PREFERENCE;
    return "";
};

export const resolveCrossSellDisplayItems = (params: {
    itemId: string;
    dishMap: Record<string, CrossSellNormalItem>;
    crossSellConfig?: CrossSellConfig;
    /**
     * Cart `menu_item_id`s frozen when the cross-sell panel was opened (last ADD_ENTRY).
     * Passed from `crossSell.entries[].cartItemIdsAtOpen`.
     */
    cartItemIdsAtOpen?: string[];
    /** Live cart line `menu_item_id`s. Used with the snapshot so items removed on the cart screen show again, while adds from this widget stay visible until the panel is re-opened. */
    currentCartMenuItemIds?: string[];
    preference: string;
    appliedFilters?: AppliedFilters;
}): CrossSellNormalItem[] => {
    const {
        itemId,
        dishMap,
        crossSellConfig,
        cartItemIdsAtOpen,
        currentCartMenuItemIds,
        preference,
        appliedFilters,
    } = params;
    const candidateIds =
        crossSellConfig?.crossSell?.crossSellPerItem?.crossSellMap?.[itemId]?.rawChildItems?.map(
            (i) => i.id
        ) ?? [];

    const resolved = candidateIds
        .map((id) => dishMap[id])
        .filter((info): info is CrossSellNormalItem => Boolean(info));

    const snapshotSet = cartItemIdsAtOpen !== undefined ? new Set(cartItemIdsAtOpen) : null;
    const currentSet =
        currentCartMenuItemIds !== undefined ? new Set(currentCartMenuItemIds) : null;

    const notInCart = resolved.filter((info) => {
        const id = String(info.id ?? "");
        if (snapshotSet === null) {
            return currentSet ? !currentSet.has(id) : true;
        }
        if (currentSet === null) {
            return !snapshotSet.has(id);
        }
        return !(currentSet.has(id) && snapshotSet.has(id));
    });

    const isExplicitVegPreference =
        preference === VEG_PREFERENCE || preference === NONVEG_PREFERENCE;
    const effectivePreference = isExplicitVegPreference
        ? preference
        : derivePreferenceFromFilters(appliedFilters);

    const filtered = notInCart.filter((info) => matchesPreference(info, effectivePreference));

    const constraints = crossSellConfig?.crossSell?.crossSellConstraints;
    const minItems = constraints?.minItems ?? CROSS_SELL_CONSTRAINT_DEFAULTS.MIN_ITEMS;
    const maxItems = constraints?.maxItems ?? CROSS_SELL_CONSTRAINT_DEFAULTS.MAX_ITEMS;
    if (filtered.length < minItems) return [];
    return filtered.slice(0, maxItems);
};
